import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';
import { getSourceBrandLocationsEndpointIdentifier } from './use-get-source-brand-locations-api';

const endpoint = 'ums/v1/crm/management/source-brand-locations';
export const deleteSourceBrandLocationEndpointIdentifier = endpoint;

type TData = void;
type TError = AxiosError<{ message: string }>;
type Options = UseMutationOptions<TData, TError, number>;
type ReturnType = UseMutationResult<TData, TError, number>;

export function useDeleteSourceBrandLocationApi(options?: Options): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();

  const mutationFn = async (id: number) => {
    try {
      await http.delete(`${endpoint}/${id}`);

      toast({
        description: 'Source brand location deleted successfully',
        status: 'success',
      });
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  const { ...otherOptions } = options || {};

  return useMutationWrapper(mutationFn, {
    onSuccess: () => {
      // Only invalidate the main list queries, location details will be irrelevant after deletion
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [queryEndpoint, params] = query.queryKey;
          return (
            queryEndpoint === getSourceBrandLocationsEndpointIdentifier &&
            typeof params === 'object' &&
            params !== null &&
            !Array.isArray(params)
          );
        },
      });
    },
    ...otherOptions,
  });
}
